import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination as SwiperPagination } from "swiper/modules";
import { useNavigate, useParams } from "react-router-dom";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "../../../assets/css/PhotoNewsSection.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_IMAGE = `${BACKEND_URL}upload/general_images/no_camera-photo.png`;

function PhotoNewsSection() {
  const navigate = useNavigate();
  const { section, id, albumId } = useParams();

  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [albumLoading, setAlbumLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 12,
  });

  const formatDate = (dateStr) => {
    if (!dateStr) {
      return { month: "N/A", day: "--", year: "----" };
    }

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
      return { month: "N/A", day: "--", year: "----" };
    }

    return {
      month: date.toLocaleString("en-GB", { month: "short" }).toUpperCase(),
      day: String(date.getDate()).padStart(2, "0"),
      year: date.getFullYear(),
    };
  };

  const truncate = (text, maxLength = 100) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength).trim()}…`
      : text;
  };

  const loadAlbums = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setLoadError(false);

        const perPage = section === "Photo Gallery" && id === "12" ? 12 : 3;

        const response = await axios.get(
          `${BASE_URL}galdata?page=${page}&per_page=${perPage}`,
        );

        setAlbums(response?.data?.allData || []);
        setPagination(
          response?.data?.pagination || {
            current_page: page,
            last_page: 1,
            total: 0,
            per_page: perPage,
          },
        );
      } catch (error) {
        console.error("Gallery load failed:", error);
        setAlbums([]);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [section, id],
  );

  useEffect(() => {
    if (
      section !== "Home" &&
      section !== undefined &&
      section !== "Photo Gallery"
    ) {
      loadAlbums(Math.floor(Math.random() * 5) + 1);
    } else {
      loadAlbums(1);
    }
  }, [loadAlbums, section, id]);

  const closeAlbum = useCallback(() => {
    setShowModal(false);
    setActiveAlbum(null);
    navigate(`/Photo Gallery/${id}`);
  }, [navigate, id]);

  useEffect(() => {
    if (!albumId) {
      setShowModal(false);
      setActiveAlbum(null);
      return;
    }

    let cancelled = false;

    const fetchAlbum = async () => {
      setAlbumLoading(true);

      try {
        const response = await axios.get(`${BASE_URL}galdata/${albumId}`);
        const album = response?.data?.album;

        if (!album || cancelled) return;

        setActiveAlbum({
          trackerf: album.trackerf,
          galleryTithle: album.galleryTithle,
          caption: album.caption,
          datesd: album.datesd,
          images: album.photos || [],
        });

        setShowModal(true);
      } catch (error) {
        console.error("Unable to load photo album:", error);
      } finally {
        if (!cancelled) setAlbumLoading(false);
      }
    };

    fetchAlbum();

    return () => {
      cancelled = true;
    };
  }, [albumId]);

  const goToPage = (nextPage) => {
    if (
      nextPage >= 1 &&
      nextPage <= pagination.last_page &&
      nextPage !== pagination.current_page
    ) {
      loadAlbums(nextPage);
    }
  };

  const handleOpenAlbum = async (album) => {
    navigate(`/Photo Gallery/${id}/album/${album.trackerf}`);

    setActiveAlbum({
      trackerf: album.trackerf,
      galleryTithle: album.galleryTithle,
      caption: album.caption,
      datesd: album.datesd,
      images: album.photos || [],
    });

    setShowModal(true);

    // Load the complete album so the slideshow includes all photographs.
    try {
      const response = await axios.get(`${BASE_URL}galdata/${album.trackerf}`);

      const fullAlbum = response?.data?.album;

      if (fullAlbum) {
        setActiveAlbum({
          trackerf: fullAlbum.trackerf,
          galleryTithle: fullAlbum.galleryTithle,
          caption: fullAlbum.caption,
          datesd: fullAlbum.datesd,
          images: fullAlbum.photos || [],
        });
      }
    } catch (error) {
      console.error("Unable to load album photographs:", error);
    }
  };

  const shareOnFacebook = () => {
    const shareUrl = encodeURIComponent(window.location.href);

    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  if (!loading && albums.length === 0 && !loadError) {
    return null;
  }

  return (
    <section
      id="photo-news"
      className="photo-news section"
      aria-label="Photo news and gallery"
    >
      <div className="container">
        <header className="pn-heading">
          <div className="pn-heading-copy">
            <span className="pn-eyebrow">
              <i className="fa-regular fa-images" aria-hidden="true" />
              Official Photo Gallery
            </span>

            <h2>
              Photos <span>News</span>
            </h2>

            {/* <p>
              Snapshots from the frontlines — projects, events and activities
              of the Federal Ministry of Housing and Urban Development.
            </p> */}
          </div>

          <div className="pn-heading-mark" aria-hidden="true">
            <i className="fa-solid fa-camera-retro" />
          </div>
        </header>

        {loading && (
          <div className="pn-state" role="status">
            <span className="pn-spinner" />
            <p>Loading photo gallery…</p>
          </div>
        )}

        {!loading && loadError && (
          <div className="pn-state pn-state-error" role="alert">
            <i className="fa-solid fa-triangle-exclamation" />
            <p>We could not load the photo gallery. Please try again.</p>
            <button
              type="button"
              className="pn-button"
              onClick={() => loadAlbums(pagination.current_page || 1)}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !loadError && albums.length > 0 && (
          <>
            <div className="pn-gallery-grid">
              {albums.map((album) => {
                const { month, day, year } = formatDate(album.datesd);

                const imageUrl = album.pic
                  ? `${BACKEND_URL}upload/${album.pic}`
                  : FALLBACK_IMAGE;

                return (
                  <article className="pn-card" key={album.trackerf}>
                    <button
                      type="button"
                      className="pn-card-image-button"
                      onClick={() => handleOpenAlbum(album)}
                      aria-label={`Open album: ${album.galleryTithle || "Photo gallery"}`}
                    >
                      <div className="pn-card-image-wrap">
                        <img
                          src={imageUrl}
                          alt={album.galleryTithle || "Gallery photograph"}
                          loading="lazy"
                          decoding="async"
                          className="pn-card-image"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = FALLBACK_IMAGE;
                          }}
                        />

                        <div className="pn-image-overlay">
                          <span className="pn-view-album">
                            <i className="fa-regular fa-images" />
                            View Album
                          </span>
                        </div>

                        <div className="pn-date-badge">
                          <span className="pn-date-month">{month}</span>
                          <strong>{day}</strong>
                          <span className="pn-date-year">{year}</span>
                        </div>

                        <span className="pn-photo-count">
                          <i className="fa-solid fa-camera" />
                          {album.photo_count || 0} Photos
                        </span>
                      </div>
                    </button>

                    <div className="pn-card-body">
                      <span className="pn-card-category">
                        <span className="pn-category-dot" />
                        Ministry in Pictures
                      </span>

                      <h3>{truncate(album.galleryTithle, 100)}</h3>

                      <button
                        type="button"
                        className="pn-card-link"
                        onClick={() => handleOpenAlbum(album)}
                      >
                        Explore Album
                        <i className="fa-solid fa-arrow-right" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            {pagination.last_page > 1 && (
              <nav className="pn-pagination" aria-label="Gallery pagination">
                <div className="pn-pagination-info">
                  Showing page <strong>{pagination.current_page}</strong> of{" "}
                  <strong>{pagination.last_page}</strong>
                  {pagination.total > 0 && (
                    <span> · {pagination.total} albums</span>
                  )}
                </div>

                <div className="pn-pagination-buttons">
                  <button
                    type="button"
                    onClick={() => goToPage(1)}
                    disabled={pagination.current_page === 1 || loading}
                    aria-label="First page"
                  >
                    <i className="fa-solid fa-angles-left" />
                    <span>First</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => goToPage(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1 || loading}
                    aria-label="Previous page"
                  >
                    <i className="fa-solid fa-angle-left" />
                    <span>Previous</span>
                  </button>

                  <span className="pn-current-page">
                    {pagination.current_page}
                  </span>

                  <button
                    type="button"
                    onClick={() => goToPage(pagination.current_page + 1)}
                    disabled={
                      pagination.current_page === pagination.last_page ||
                      loading
                    }
                    aria-label="Next page"
                  >
                    <span>Next</span>
                    <i className="fa-solid fa-angle-right" />
                  </button>

                  <button
                    type="button"
                    onClick={() => goToPage(pagination.last_page)}
                    disabled={
                      pagination.current_page === pagination.last_page ||
                      loading
                    }
                    aria-label="Last page"
                  >
                    <span>Last</span>
                    <i className="fa-solid fa-angles-right" />
                  </button>
                </div>
              </nav>
            )}
          </>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={closeAlbum}
        size="xl"
        centered
        scrollable
        dialogClassName="pn-modal-dialog"
        contentClassName="pn-modal-content"
        aria-labelledby="pn-modal-title"
      >
        <div className="pn-modal-header">
          <div className="pn-modal-heading">
            <span className="pn-modal-eyebrow">
              <i className="fa-regular fa-images" />
              Photo Gallery
            </span>

            <h3 id="pn-modal-title">
              {activeAlbum?.galleryTithle || "Gallery Album"}
            </h3>

            {activeAlbum?.datesd && (
              <span className="pn-modal-date">
                <i className="fa-regular fa-calendar" />
                {(() => {
                  const date = formatDate(activeAlbum.datesd);
                  return `${date.month} ${date.day}, ${date.year}`;
                })()}
              </span>
            )}

            {/* {activeAlbum?.caption && (
              <p>{activeAlbum.caption}</p>
            )} */}
          </div>

          <div className="pn-modal-actions">
            <button
              type="button"
              className="pn-share-button"
              onClick={shareOnFacebook}
              aria-label="Share this album on Facebook"
            >
              <i className="fa-brands fa-facebook-f" />
              <span>Share</span>
            </button>

            <button
              type="button"
              className="pn-close-button"
              onClick={closeAlbum}
              aria-label="Close gallery"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <Modal.Body className="pn-modal-body">
          {albumLoading && !activeAlbum && (
            <div className="pn-state">
              <span className="pn-spinner" />
              <p>Loading album photographs…</p>
            </div>
          )}

          {activeAlbum && activeAlbum.images?.length > 0 && (
            <Swiper
              key={activeAlbum.trackerf}
              modules={[Navigation, SwiperPagination]}
              navigation
              pagination={{ clickable: true }}
              loop={activeAlbum.images.length > 1}
              className="pn-swiper"
            >
              {activeAlbum.images.map((image, index) => (
                <SwiperSlide key={image.id || image.pic || index}>
                  <div className="pn-slide">
                    <img
                      src={`${BACKEND_URL}upload/${image.pic}`}
                      alt={`${activeAlbum.galleryTithle || "Gallery"} — photograph ${index + 1}`}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />

                    <span className="pn-slide-counter">
                      Photograph {index + 1} of {activeAlbum.images.length}
                    </span>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {activeAlbum && !activeAlbum.images?.length && !albumLoading && (
            <div className="pn-state">
              <i className="fa-regular fa-image pn-empty-icon" />
              <p>No photographs are available in this album.</p>
            </div>
          )}
        </Modal.Body>

        <div className="pn-modal-footer">
          <span>
            <i className="fa-solid fa-camera" />
            {activeAlbum?.images?.length || 0} photographs
          </span>

          <button type="button" onClick={closeAlbum}>
            Close Gallery
          </button>
        </div>
      </Modal>
    </section>
  );
}

export default React.memo(PhotoNewsSection);
